import { useEffect, useState } from 'react';
import { resolveApiBaseURL } from '../../lib/axiosInstance';
import DEFAULT_HEADER_MENU from './menu.json';

const HEADER_MENU_ENDPOINT = 'web/header-menu';

let headerMenuSnapshot = DEFAULT_HEADER_MENU;
let headerMenuRequest = null;
const subscribers = new Set();

const notifySubscribers = () => {
    subscribers.forEach((subscriber) => subscriber(headerMenuSnapshot));
};

const hasTextValue = (value) => typeof value === 'string' && value.trim().length > 0;

const isNamelessCategoryGroup = (item, level) => (
    level === 1
    && !hasTextValue(item?.name)
    && !item?.link
    && Array.isArray(item?.subcategories)
    && item.subcategories.length > 0
);

const isValidMenuItem = (item, level = 0) => {
    if (!item || typeof item !== 'object') {
        return false;
    }

    if (!hasTextValue(item.id)) {
        return false;
    }

    if (!hasTextValue(item.name) && !isNamelessCategoryGroup(item, level)) {
        return false;
    }

    if (item.categories !== undefined && !Array.isArray(item.categories)) {
        return false;
    }

    if (item.subcategories !== undefined && !Array.isArray(item.subcategories)) {
        return false;
    }

    const categories = Array.isArray(item.categories) ? item.categories : [];
    const subcategories = Array.isArray(item.subcategories) ? item.subcategories : [];

    return categories.every((category) => isValidMenuItem(category, level + 1))
        && subcategories.every((subcategory) => isValidMenuItem(subcategory, level + 1));
};

export const isValidHeaderMenu = (menu) => {
    if (!menu || typeof menu !== 'object' || !Array.isArray(menu.mainCategories)) {
        return false;
    }

    return menu.mainCategories.every((item) => isValidMenuItem(item));
};

export const normalizeHeaderMenuResponse = (payload) => {
    const candidate = payload?.data?.mainCategories ? payload.data : payload;
    return isValidHeaderMenu(candidate) ? candidate : null;
};

export const fetchHeaderMenu = async ({ signal } = {}) => {
    const baseUrl = resolveApiBaseURL();
    const response = await fetch(`${baseUrl}/${HEADER_MENU_ENDPOINT}`, {
        headers: {
            Accept: 'application/json',
        },
        signal,
    });

    if (!response.ok) {
        throw new Error(`Header menu request failed (${response.status})`);
    }

    return response.json();
};

export const loadHeaderMenu = async ({ requestMenu = fetchHeaderMenu, signal } = {}) => {
    if (!headerMenuRequest) {
        headerMenuRequest = requestMenu({ signal })
            .then((payload) => {
                const remoteMenu = normalizeHeaderMenuResponse(payload);
                if (!remoteMenu) {
                    return headerMenuSnapshot;
                }

                headerMenuSnapshot = remoteMenu;
                notifySubscribers();
                return headerMenuSnapshot;
            })
            .catch(() => headerMenuSnapshot);
    }

    return headerMenuRequest;
};

export const useHeaderMenu = () => {
    const [headerMenu, setHeaderMenu] = useState(headerMenuSnapshot);

    useEffect(() => {
        subscribers.add(setHeaderMenu);
        return () => {
            subscribers.delete(setHeaderMenu);
        };
    }, []);

    useEffect(() => {
        loadHeaderMenu();
    }, []);

    return headerMenu;
};

export const resetHeaderMenuForTests = () => {
    headerMenuSnapshot = DEFAULT_HEADER_MENU;
    headerMenuRequest = null;
    subscribers.clear();
};

export default useHeaderMenu;
